import { useCallback, useEffect, useRef, useState } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"
import { WebsocketProvider } from "y-websocket"

import { CollaborationService } from "services/collaborationService"
import { htmlToPlainText, stripCommentSpans } from "utils/editorUtils"
import { Variant } from "types/questionnaire.types"

interface UseQuestionnaireSaveProps {
  /** The TipTap editor instance */
  editor: TiptapEditor | null
  /** Whether collaboration is enabled */
  collaborationEnabled: boolean
  /** Whether the collaboration WebSocket is connected */
  isConnected: boolean
  /** Whether the collaboration session is initialized */
  isCollaborationReady: boolean
  /** Current selected variant name */
  currentVariant: string
  /** The variants data */
  variants: Variant[]
  /** Full questionnaire text content */
  fullQuestionnaire: string
  /** Function to fetch the latest questionnaire from backend */
  fetchLatestQuestionnaire?: () => Promise<string>
  /** Function to save the questionnaire content */
  saveQuestionnaire: (
    updatedVariantsParam?: Variant[],
    collaborativeContent?: string,
  ) => Promise<void>
  /** Converts editor content to the backend text format */
  convertToTextFormat: (
    currentContent: string,
    variants: Variant[],
    currentVariant?: string,
    fullQuestionnaire?: string,
  ) => string
  /** Returns the WebSocket provider for a variant */
  getProvider: (variantName: string) => WebsocketProvider | null
  /** The collaboration service instance */
  collaborationService: CollaborationService | null
  /** Whether the editor content has unsaved changes */
  contentChanged: boolean
  /** Setter for the unsaved-changes flag */
  setContentChanged: React.Dispatch<React.SetStateAction<boolean>>
  /** Setter for the non-collaborative editor content state */
  setEditorContent: React.Dispatch<React.SetStateAction<string>>
}

interface UseQuestionnaireSaveReturn {
  isSaving: boolean
  performSave: () => Promise<void>
  manualSaveQuestionnaire: () => Promise<void>
}

/**
 * useQuestionnaireSave owns the questionnaire save workflow: the shared
 * performSave function (collaborative and plain modes), the 10-second
 * autosave interval, and manual saving via the Ctrl+S shortcut.
 */
export const useQuestionnaireSave = ({
  editor,
  collaborationEnabled,
  isConnected,
  isCollaborationReady,
  currentVariant,
  variants,
  fullQuestionnaire,
  fetchLatestQuestionnaire,
  saveQuestionnaire,
  convertToTextFormat,
  getProvider,
  collaborationService,
  contentChanged,
  setContentChanged,
  setEditorContent,
}: UseQuestionnaireSaveProps): UseQuestionnaireSaveReturn => {
  const [isSaving, setIsSaving] = useState<boolean>(false)

  /**
   * Converts HTML content back to plain text (uses utility function)
   * Strips comment spans first to prevent them from breaking the backend parser
   */
  const htmlToFilteredText = useCallback((html: string): string => {
    const cleanedHtml = stripCommentSpans(html)
    return htmlToPlainText(cleanedHtml)
  }, [])

  /**
   * Shared save function used by both autosave and manual save.
   * Fetches the latest questionnaire from the backend before saving
   * to ensure variant changes are properly preserved.
   */
  const performSave = async (): Promise<void> => {
    if (!editor) return
    // Block saves when disconnected in collaborative mode — edits stay in
    // IndexedDB and will sync via Yjs when reconnected. This prevents
    // overwriting other users' changes with stale local content.
    if (collaborationEnabled && !isConnected) return
    setIsSaving(true)

    try {
      if (isCollaborationReady && collaborationEnabled) {
        // Wait for a fresh Yjs sync before reading editor state — guarantees
        // the editor reflects the latest server state (including other users'
        // edits) before we build the save payload. 2s timeout as safety net.
        const variantKey = currentVariant === "" ? "master" : currentVariant
        const provider = getProvider(variantKey)
        if (provider && provider.wsconnected) {
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(resolve, 2000)
            const onSync = (synced: boolean) => {
              if (synced) {
                clearTimeout(timeout)
                provider.off("sync", onSync)
                resolve()
              }
            }
            provider.on("sync", onSync)
          })
        }

        const latestQuestionnaire = fetchLatestQuestionnaire
          ? await fetchLatestQuestionnaire()
          : fullQuestionnaire
        const currentHTML = editor.getHTML()
        const currentTextContent = htmlToFilteredText(currentHTML)
        const collaborativeText = convertToTextFormat(
          currentTextContent,
          variants,
          currentVariant,
          latestQuestionnaire,
        )
        await saveQuestionnaire(undefined, collaborativeText)
        // Record save timestamp for stale Yjs detection
        if (collaborationService) {
          const variantKey = currentVariant === "" ? "master" : currentVariant
          collaborationService.recordSaveTimestamp(variantKey)
        }
      } else {
        setEditorContent(editor.getHTML())
        await saveQuestionnaire()
      }
      setContentChanged(false)
      setTimeout(() => setIsSaving(false), 1000)
    } catch (error) {
      console.error("Error saving questionnaire:", error)
      setIsSaving(false)
    }
  }

  // Auto save the questionnaire every 10 seconds
  // In collaborative mode, only save when connected to prevent overwrites
  useEffect(() => {
    let saveInterval: NodeJS.Timeout | undefined

    const shouldSave = collaborationEnabled
      ? editor && isConnected && contentChanged // Only save when connected AND content changed
      : editor && contentChanged // Only save with changes in non-collaborative

    if (shouldSave) {
      saveInterval = setInterval(() => {
        performSave()
      }, 10000) // 10 seconds
    }

    return () => {
      if (saveInterval) {
        clearInterval(saveInterval)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editor,
    saveQuestionnaire,
    contentChanged,
    isCollaborationReady,
    collaborationEnabled,
    isConnected,
    htmlToFilteredText,
    convertToTextFormat,
    variants,
    currentVariant,
    fullQuestionnaire,
    fetchLatestQuestionnaire,
  ])

  /**
   * Manually saves the questionnaire content.
   * Delegates to the shared performSave function.
   */
  const manualSaveQuestionnaire = (): Promise<void> => performSave()

  /**
   * Keyboard shortcut handler for Save (Ctrl+S)
   */
  const manualSaveRef = useRef(manualSaveQuestionnaire)
  manualSaveRef.current = manualSaveQuestionnaire

  useEffect(() => {
    const handleCtrlS = (e: KeyboardEvent) => {
      if (e.ctrlKey && !e.shiftKey && e.key === "s") {
        e.preventDefault()
        manualSaveRef.current()
      }
    }

    window.addEventListener("keydown", handleCtrlS)
    return () => window.removeEventListener("keydown", handleCtrlS)
  }, [])

  return {
    isSaving,
    performSave,
    manualSaveQuestionnaire,
  }
}
